import { z } from 'zod';
import { ContactLocaleSchema, ContactOperationTypeSchema, ContactPhoneSchema } from './contact';

/**
 * Diagnóstico de perdas da landing (`apps/marketing` → `/{locale}/loss-calculator`).
 *
 * É a porta de captação de lead do site: em vez de mandar o visitante direto
 * para `/subscribe`, a calculadora estima quanto a cozinha dele perde por mês e
 * troca o diagnóstico completo pelo contato.
 *
 * Fluxo (CLAUDE.md "Backend é a fonte da verdade"), espelhando o de
 * `contact.ts`:
 *  1. A página posta para a Function HTTP `submitLossDiagnosis`, que valida na
 *     borda com `SubmitLossDiagnosisInputSchema` e grava em
 *     `/lossDiagnoses/{id}`. A persistência é o efeito garantido.
 *  2. O trigger `crmOnLossDiagnosisCreated` (codebase `crm`) cria o lead em
 *     `crm_boards/{boardId}/crm_leads/{leadId}` e avisa vendas@ por e-mail.
 *  3. Leitura só super-admin nas Rules; o cliente público nunca lê nem escreve.
 */

/**
 * Como a cozinha controla validade hoje.
 *
 * Serve para QUALIFICAR o lead (o SDR abre a conversa sabendo se a casa usa
 * caneta ou planilha) e **não** entra no cálculo da perda: não existe estudo
 * ligando método de controle a um multiplicador de desperdício, e inventar um
 * seria vender número que não temos como defender.
 */
export const LossControlMethodSchema = z.enum([
  /** Nenhum controle formal — memória e olfato. */
  'NONE',
  /** Etiqueta escrita à mão, fita crepe, caneta. */
  'HANDWRITTEN',
  /** Planilha ou caderno. */
  'SPREADSHEET',
  /** Já usa algum sistema/etiquetas impressas. */
  'SYSTEM',
]);
export type LossControlMethod = z.infer<typeof LossControlMethodSchema>;
export const ALL_LOSS_CONTROL_METHODS = LossControlMethodSchema.options;

/**
 * Teto de sanidade do CMV mensal informado: 100 milhões em cents (R$, US$ ou
 * €). Não é regra de negócio — é anti-lixo, para um bot não gravar
 * `Number.MAX_VALUE` e o e-mail de vendas sair com um número absurdo.
 */
export const MAX_MONTHLY_CMV_CENTS = 10_000_000_000;

/**
 * Moeda em que o visitante informou o CMV — e em que a estimativa foi exibida.
 *
 * A calculadora existe nos 6 locales do site (#683) e a moeda vem do locale:
 * pt → BRL, en/es → USD (LATAM cobra em USD, #52), it/fr/de → EUR. O mapa
 * mora no marketing (`lossEstimate.ts`); aqui só o enum, porque é o servidor
 * que grava e a nota do SDR que formata. Sem este campo, um CMV de 60.000
 * dólares viraria "R$ 60.000" no e-mail de vendas — silenciosamente.
 */
export const LossDiagnosisCurrencySchema = z.enum(['BRL', 'USD', 'EUR']);
export type LossDiagnosisCurrency = z.infer<typeof LossDiagnosisCurrencySchema>;
export const ALL_LOSS_DIAGNOSIS_CURRENCIES = LossDiagnosisCurrencySchema.options;

/**
 * Input do formulário público (borda HTTP `submitLossDiagnosis`).
 *
 * `website` é honeypot, mesmo contrato do form de contato: campo oculto que
 * humanos deixam vazio e bots preenchem. Fica fora da validação estrita — a
 * Function checa o valor cru e responde 200 silencioso quando preenchido, para
 * não sinalizar o bloqueio ao bot.
 *
 * `estimateMinCents`/`estimateMaxCents` são o que o visitante VIU na tela.
 * Guardamos porque é o número ao qual ele reagiu (o SDR abre a conversa com
 * ele), mas a fonte da verdade do diagnóstico são os campos crus — o cálculo
 * roda no cliente e, por definição, é forjável.
 */
export const SubmitLossDiagnosisInputSchema = z.object({
  /**
   * Id do envio gerado no cliente, também empurrado no `dataLayer`. Existe
   * para deduplicar a conversão: o Pixel do Meta manda o `Lead` do navegador e
   * um envio server-side pelo CAPI mandaria o mesmo evento — sem um id comum,
   * a mesma pessoa contaria como duas conversões.
   */
  eventId: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(2).max(120),
  email: z.email().max(200),
  phone: ContactPhoneSchema,
  locale: ContactLocaleSchema,
  /**
   * `default('BRL')`, não `catch`: a página é HTML estático em cache, e um
   * visitante com a versão anterior posta sem o campo — o valor "quando falta"
   * é o da única moeda que existia até então. Moeda fora do enum continua 400.
   */
  currency: LossDiagnosisCurrencySchema.default('BRL'),
  operationType: ContactOperationTypeSchema,
  /** CMV mensal aproximado, em cents de `currency` (`project_money_cents_single_source`). */
  monthlyCmvCents: z.number().int().positive().max(MAX_MONTHLY_CMV_CENTS),
  /** Quantas unidades/lojas a operação tem. */
  units: z.number().int().min(1).max(999),
  /** Quantas pessoas trabalham na cozinha. */
  kitchenHeadcount: z.number().int().min(1).max(9999),
  currentControl: LossControlMethodSchema,
  /** Percentual de descarte que o próprio visitante informa (0–100). */
  selfReportedWastePct: z.number().min(0).max(100).optional(),
  estimateMinCents: z.number().int().min(0).max(MAX_MONTHLY_CMV_CENTS),
  estimateMaxCents: z.number().int().min(0).max(MAX_MONTHLY_CMV_CENTS),
  website: z.string().max(200).optional(),
});
export type SubmitLossDiagnosisInput = z.infer<typeof SubmitLossDiagnosisInputSchema>;

/**
 * Documento `/lossDiagnoses/{id}`.
 *
 * `createdAt`/`emailSentAt` são Firestore `Timestamp` (tipados `unknown` — o
 * cliente materializa em millis ao ler, como `AuditEventSchema.at`).
 * `crmLeadId`/`emailSentAt` são os guards de idempotência do trigger: cada
 * efeito colateral acontece uma vez só, mesmo em reentrega.
 */
export const LossDiagnosisSchema = z.object({
  /** Id do envio, para dedupe da conversão. `nullish` por compat. */
  eventId: z.string().nullish(),
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  locale: ContactLocaleSchema,
  /** Docs anteriores à #683 não têm o campo: eram todos em BRL. */
  currency: LossDiagnosisCurrencySchema.default('BRL'),
  operationType: ContactOperationTypeSchema,
  monthlyCmvCents: z.number(),
  units: z.number(),
  kitchenHeadcount: z.number(),
  currentControl: LossControlMethodSchema,
  selfReportedWastePct: z.number().nullable(),
  estimateMinCents: z.number(),
  estimateMaxCents: z.number(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.unknown(),
  /** Id do lead criado no CRM, ou do lead existente ao qual anexamos a nota. */
  crmLeadId: z.string().nullish(),
  /** Quadro onde o lead caiu — junto com `crmLeadId` monta o link do CRM. */
  crmBoardId: z.string().nullish(),
  emailSentAt: z.unknown().nullish(),
});
export type LossDiagnosis = z.infer<typeof LossDiagnosisSchema>;

/** Doc ID do roteamento dentro de `crm_appConfig`. */
export const CRM_INBOUND_ROUTING_DOC_ID = 'inboundRouting';

/**
 * `crm_appConfig/inboundRouting` — para onde vai o lead que entra pelo site.
 *
 * Mora aqui (e não em `crm.ts`) para manter a feature coesa num arquivo só; o
 * prefixo `Crm` segue a convenção dos schemas do CRM.
 *
 * Leitura TOLERANTE no servidor (safeParse + fallback), como
 * `CrmOnboardingTemplateSchema`: doc ausente ou inválido não pode derrubar o
 * trigger — o diagnóstico já está gravado e o lead não se perde.
 */
export const CrmInboundRoutingSchema = z.object({
  boardId: z.string().min(1).max(128),
  columnId: z.string().min(1).max(128),
  ownerUserId: z.string().min(1).max(128),
});
export type CrmInboundRouting = z.infer<typeof CrmInboundRoutingSchema>;

/** Input da callable ADMIN `crmSetInboundRouting`. */
export const CrmSetInboundRoutingInputSchema = CrmInboundRoutingSchema;
export type CrmSetInboundRoutingInput = z.infer<typeof CrmSetInboundRoutingInputSchema>;

/**
 * Valor de `crm_leads.source` para lead nascido na calculadora. `source` é
 * string livre no `CrmLeadSchema`; esta constante é a convenção para não
 * espalhar literal pelo código.
 */
export const CRM_LEAD_SOURCE_WEBSITE_CALCULATOR = 'WEBSITE_CALCULATOR';
